import { useFragment } from '@apollo/client'
import {
  EditSurveySurveyFragmentDoc,
  EngagementUpdateSurveySettingsDocument,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { SurveyWizard } from 'modules/engagement_surveys/components/SurveyWizard'

interface Props {
  surveyId: number
  onClose: () => void
  onSuccess: () => void
}

export function EditSurveyWizard({ surveyId, onClose, onSuccess }: Props) {
  const { data: survey } = useFragment({
    fragment: EditSurveySurveyFragmentDoc,
    fragmentName: 'EditSurveySurvey',
    from: {
      __typename: 'SurveysSurvey',
      id: surveyId,
    },
  })

  const surveyForm = survey?.form

  const [updateSurvey] = useMutation(EngagementUpdateSurveySettingsDocument, {
    extractorFn: (data) => data.surveysMutations.updateSurveysSurvey,
  })

  const respondentStrategy = survey?.respondentStrategy
    ? {
        strategy: survey.respondentStrategy.strategy ?? ('all_employees' as const),
        arguments: compact(survey.respondentStrategy.arguments),
      }
    : { strategy: 'all_employees' as const, arguments: [] }

  const editors =
    survey?.editors
      ?.map((editor) => editor?.employee)
      .filter((editor) => editor !== undefined)
      ?.map((editor) => ({
        id: editor.id ?? 0,
        fullName: editor.fullName ?? '',
        firstName: editor.firstName ?? '',
        lastName: editor.lastName ?? '',
        access: {
          id: editor.access?.id ?? 0,
          avatar: editor.access?.avatar ?? '',
        },
      })) ?? []

  return (
    <SurveyWizard
      title={i18n.t('surveys.edit_survey_dialog.title')}
      initialValues={{
        title: surveyForm?.title || '',
        description: surveyForm?.description || '',
        anonymous: surveyForm?.anonymous || false,
        restrictedViewersEnabled: survey?.restrictedViewersEnabled || false,
        editors,
        respondentStrategy,
      }}
      onClose={onClose}
      onSubmit={async (data) => {
        const dataRespondentStrategy = data.respondentStrategy

        const respondentStrategyInput =
          dataRespondentStrategy.strategy &&
          dataRespondentStrategy.arguments?.filter((argument) => argument !== undefined)
            ? {
                strategy: dataRespondentStrategy.strategy,
                arguments:
                  dataRespondentStrategy.arguments.filter((argument) => argument !== undefined) ||
                  [],
              }
            : undefined

        if (!respondentStrategyInput) {
          throw new Error('Respondent strategy is required')
        }

        const result = await updateSurvey({
          variables: {
            surveyId,
            title: data.title,
            anonymous: data.anonymous,
            description: data.description,
            respondentStrategy: respondentStrategyInput,
            restrictedViewersEnabled: data.restrictedViewersEnabled,
            editorIds:
              data.editors.map((editor) => editor.id).filter((id) => id !== undefined) || [],
          },
        })
        const handled = handleResult(result, () => null)

        if (handled.success && handled.data?.survey) {
          onSuccess()
        }
      }}
    />
  )
}
